import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, ClientverseHistoryVersion, ClientverseSchemaExport, StagingCanonicalUploadBody, StagingCanonicalVersion } from '../api/admin';

// Global Modules > Data Mapping settings. Only the ClientVerse Core export
// lives here now — System/Staging schemas and their mappings moved to the
// per-client admin tab on each client's detail page.
export default function AdminModuleDataMapping() {
  const [error, setError] = useState<string | null>(null);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <h1 style={{ color: 'var(--mi-navy)', margin: 0 }}>Data Mapping</h1>
      </div>
      <p style={{ color: 'var(--mi-muted)', marginTop: 4 }}>
        ClientVerse Core is synced from Salesforce and shared across all clients. The System and Staging schemas (and their mappings) are per-client &mdash; manage them under <em>Admin &rsaquo; Clients &rsaquo; (client) &rsaquo; Data Mapping</em>.
      </p>

      {error && <div style={errBox}>{error}</div>}

      <h2 style={sectionHeading}>ClientVerse Core</h2>
      <ClientverseCoreCard onError={setError} />

      <h2 style={sectionHeading}>Staging Templates (Canonical)</h2>
      <StagingCanonicalCard onError={setError} />

      <h2 style={sectionHeading}>Documentation</h2>
      <DocumentationCard />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Card: ClientVerse Core (list + export + change history) — same list/action
// styling as StagingCanonicalCard below, just with no upload UI since this is
// sync-only.
// -----------------------------------------------------------------------------
function ClientverseCoreCard({ onError }: { onError: (msg: string) => void }) {
  const { getToken } = useAuth();
  const [versions, setVersions] = useState<ClientverseHistoryVersion[] | null>(null);
  const [busy, setBusy] = useState<null | { versionId: number; kind: 'json' | 'md' }>(null);

  const load = async () => {
    try {
      const t = await getToken();
      const data = await adminApi.getClientverseHistory(t);
      setVersions([...data.versions].reverse()); // newest first, matching Staging's ordering
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const exportSchema = async (v: ClientverseHistoryVersion) => {
    setBusy({ versionId: v.id, kind: 'json' });
    try {
      const t = await getToken();
      const data = await adminApi.exportClientverseSchema(t, v.id);
      downloadJson(`clientverse-schema-v${data.clientverse_version}.json`, data);
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const exportDictionary = async (v: ClientverseHistoryVersion) => {
    setBusy({ versionId: v.id, kind: 'md' });
    try {
      const t = await getToken();
      const data = await adminApi.exportClientverseSchema(t, v.id);
      const md = renderDataDictionaryMarkdown(data);
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
      triggerDownload(blob, `clientverse-data-dictionary-v${data.clientverse_version}.md`);
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-purple)' }}>
      <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13, color: 'var(--mi-muted)' }}>
        Synced from Salesforce by the <code>mi-job-salesforce-schema</code> Azure Function. No upload UI &mdash; managed by the scheduled job.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <Link to="/admin/modules/data-mapping/clientverse/core/browse" style={primaryBtn}>
          Browse &amp; Document
        </Link>
        <Link to="/admin/modules/data-mapping/clientverse/history" style={{ ...outlineBtn, textDecoration: 'none' }}>
          Change History
        </Link>
      </div>

      {versions === null && <div style={{ color: 'var(--mi-muted)', fontSize: 13 }}>Loading…</div>}
      {versions && versions.length === 0 && (
        <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 8 }}>No versions yet.</div>
      )}
      {versions && versions.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Version</th>
              <th style={th}>Synced</th>
              <th style={th}>Objects</th>
              <th style={th}>Fields</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} style={{ borderTop: '1px solid var(--mi-border)' }}>
                <td style={td}><code style={{ fontWeight: 600 }}>v{v.version_string}</code></td>
                <td style={td}>{new Date(v.first_synced_at).toLocaleString()}</td>
                <td style={td}>{v.total_objects.toLocaleString()}</td>
                <td style={td}>{v.total_fields.toLocaleString()}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <Link
                    to={`/admin/modules/data-mapping/clientverse/history/${v.id}`}
                    style={{ ...outlineBtn, textDecoration: 'none' }}
                    title="See the objects and fields added or deprecated at this version"
                  >
                    View Changes
                  </Link>
                  {' '}
                  <button onClick={() => exportSchema(v)} disabled={busy !== null} style={outlineBtn}>
                    {busy?.versionId === v.id && busy.kind === 'json' ? 'Exporting…' : 'Export JSON'}
                  </button>
                  {' '}
                  <button onClick={() => exportDictionary(v)} disabled={busy !== null} style={outlineBtn}>
                    {busy?.versionId === v.id && busy.kind === 'md' ? 'Exporting…' : 'Export Data Dictionary MD'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Card: Documentation — client-facing write-ups derived from the internal
// planning docs, broken into standalone pages. Each entry here is one such
// write-up; more land as sibling rows as they're built out.
// -----------------------------------------------------------------------------
function DocumentationCard() {
  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-navy)' }}>
      <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13, color: 'var(--mi-muted)' }}>
        Client-facing write-ups of the data-mapping plans, kept separate from the internal engineering docs in <code>_documents/</code>.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>Title</th>
            <th style={th}>Description</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderTop: '1px solid var(--mi-border)' }}>
            <td style={{ ...td, fontWeight: 600, color: 'var(--mi-navy)' }}>System to ClientVerse Core</td>
            <td style={{ ...td, color: 'var(--mi-muted)' }}>
              The phased plan for how data flows from your source systems into ClientVerse Core, broken into one page per step.
            </td>
            <td style={{ ...td, textAlign: 'right' }}>
              <Link to="/admin/modules/data-mapping/system-to-clientverse" style={{ ...outlineBtn, textDecoration: 'none' }}>
                View →
              </Link>
            </td>
          </tr>
          <tr style={{ borderTop: '1px solid var(--mi-border)' }}>
            <td style={{ ...td, fontWeight: 600, color: 'var(--mi-navy)' }}>System Load Order</td>
            <td style={{ ...td, color: 'var(--mi-muted)' }}>
              Within each source system, the order its own tables need to load in — broken into one page per system, starting with Aderant (PMS) and InterAction (CRM).
            </td>
            <td style={{ ...td, textAlign: 'right' }}>
              <Link to="/admin/modules/data-mapping/system-load-order" style={{ ...outlineBtn, textDecoration: 'none' }}>
                View →
              </Link>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Card: canonical staging templates (list + upload + export + delete)
// -----------------------------------------------------------------------------
function StagingCanonicalCard({ onError }: { onError: (msg: string) => void }) {
  const { getToken } = useAuth();
  const [versions, setVersions] = useState<StagingCanonicalVersion[] | null>(null);
  const [busy, setBusy] = useState<null | { kind: 'upload' | 'export' | 'delete'; label: string }>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    try {
      const t = await getToken();
      setVersions(await adminApi.listStagingCanonicalVersions(t));
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy({ kind: 'upload', label: 'Uploading…' });
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed.schema_version !== 'string' || !Array.isArray(parsed.objects)) {
        throw new Error('Invalid file: expected { "schema_version": "…", "objects": [ … ] } at the top level.');
      }
      const t = await getToken();
      await adminApi.uploadStagingCanonicalVersion(t, parsed as StagingCanonicalUploadBody);
      await load();
    } catch (err) { onError(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(null); }
  };

  const exportVersion = async (v: StagingCanonicalVersion) => {
    setBusy({ kind: 'export', label: `Exporting v${v.schema_version}…` });
    try {
      const t = await getToken();
      const body = await adminApi.exportStagingCanonicalVersion(t, v.schema_version);
      const blob = new Blob([JSON.stringify(body, null, 2)], { type: 'application/json' });
      triggerDownload(blob, `staging-canonical-v${v.schema_version}.json`);
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const deleteVersion = async (v: StagingCanonicalVersion) => {
    if (!confirm(`Delete template v${v.schema_version}? This removes ${v.objects_count} objects and ${v.fields_count} fields from the template. Clients started from it keep their own copies.`)) {
      return;
    }
    setBusy({ kind: 'delete', label: `Deleting v${v.schema_version}…` });
    try {
      const t = await getToken();
      await adminApi.deleteStagingCanonicalVersion(t, v.schema_version);
      await load();
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-green)' }}>
      <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13, color: 'var(--mi-muted)' }}>
        Canonical staging templates. Each version is a full snapshot of the staging tables and fields upstream of ClientVerse Core, descriptions and samples included. Every client owns its own staging schema: start one from a template on <em>Admin &rsaquo; Clients &rsaquo; (client)</em>. The copy is then the client&rsquo;s, so editing a template here changes no client.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={() => fileInput.current?.click()} disabled={busy !== null} className="btn-primary">
          {busy?.kind === 'upload' ? busy.label : 'Upload New Version'}
        </button>
        <input ref={fileInput} type="file" accept="application/json,.json" onChange={onFile} style={{ display: 'none' }} />
        <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
          File must include a top-level <code>schema_version</code> plus an <code>objects[]</code> array.
        </span>
      </div>

      {versions === null && <div style={{ color: 'var(--mi-muted)', fontSize: 13 }}>Loading…</div>}
      {versions && versions.length === 0 && (
        <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 8 }}>No canonical versions yet.</div>
      )}
      {versions && versions.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Version</th>
              <th style={th}>Uploaded</th>
              <th style={th}>Objects</th>
              <th style={th}>Fields</th>
              <th style={th} title="Clients whose own staging schema was last started from this template">Clients started from it</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} style={{ borderTop: '1px solid var(--mi-border)' }}>
                <td style={td}><code style={{ fontWeight: 600 }}>v{v.schema_version}</code></td>
                <td style={td}>{new Date(v.uploaded_at).toLocaleString()}</td>
                <td style={td}>{v.objects_count.toLocaleString()}</td>
                <td style={td}>{v.fields_count.toLocaleString()}</td>
                <td style={td}>{v.based_clients}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <Link
                    to={`/admin/modules/data-mapping/staging/${encodeURIComponent(v.schema_version)}/browse`}
                    style={{ ...outlineBtn, textDecoration: 'none' }}
                    title="Browse the objects and fields, and edit the template's descriptions and samples"
                  >
                    Browse &amp; Document
                  </Link>
                  {' '}
                  <button onClick={() => exportVersion(v)} disabled={busy !== null} style={outlineBtn}>Download JSON</button>
                  {' '}
                  <button onClick={() => deleteVersion(v)} disabled={busy !== null} style={dangerOutlineBtn} title="Delete this template">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  triggerDownload(blob, filename);
}
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function renderDataDictionaryMarkdown(data: ClientverseSchemaExport): string {
  const escapeCell = (s: string | null | undefined): string =>
    (s ?? '').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ').trim();
  const yesNo = (b: boolean): string => (b ? 'Yes' : 'No');
  const lines: string[] = [];
  lines.push(`# ClientVerse Data Dictionary - Version ${data.clientverse_version}`);
  lines.push('');
  for (const obj of data.objects) {
    lines.push(`## ${obj.name}`);
    lines.push(`### ${obj.label || obj.name}`);
    lines.push('');
    lines.push(`Custom: ${yesNo(obj.is_custom)}`);
    lines.push('');
    lines.push('| Name | Label | Data Type | Required | Custom | Length | Reference | Picklist Values |');
    lines.push('|---|---|---|---|---|---|---|---|');
    for (const f of obj.fields) {
      const refs = f.reference_to.length === 0 ? '' : escapeCell(f.reference_to.join(', '));
      const picklists = f.picklist_values.length === 0 ? '' : escapeCell(
        f.picklist_values
          .map((pv) => (pv.label && pv.label !== pv.value ? `${pv.value} (${pv.label})` : pv.value))
          .join(', '),
      );
      lines.push(
        `| ${escapeCell(f.name)} | ${escapeCell(f.label)} | ${escapeCell(f.data_type)} | ${yesNo(f.is_required)} | ${yesNo(f.is_custom)} | ${f.length ?? ''} | ${refs} | ${picklists} |`,
      );
    }
    lines.push('');
  }
  return lines.join('\n');
}

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------
const breadcrumb: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 14, textDecoration: 'none' };
const errBox: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
const sectionHeading: React.CSSProperties = { color: 'var(--mi-navy)', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 24, marginBottom: 12 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--mi-navy)', textTransform: 'uppercase', letterSpacing: 0.5,
  borderBottom: '1px solid var(--mi-border)', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
// .btn-primary's actual chrome (padding, radius, cursor) comes from a bare
// `button` tag selector in brand.css, not a class -- so it only applies to
// real <button> elements. A <Link> styled with className="btn-primary" only
// picks up the background/color and renders squashed, with an underline on
// hover from the base `a` rule. This is the button-shaped equivalent for
// when the primary action needs to be a real link (navigation, not an
// async handler) instead.
const primaryBtn: React.CSSProperties = {
  display: 'inline-block', background: 'var(--mi-navy)', color: '#fff',
  padding: '8px 16px', borderRadius: 6, border: 'none',
  cursor: 'pointer', fontSize: 13, fontWeight: 500, textDecoration: 'none',
};
const outlineBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)',
  color: 'var(--mi-navy)', padding: '4px 10px', borderRadius: 6,
  cursor: 'pointer', fontSize: 12, fontWeight: 500,
};
const dangerOutlineBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid #f3c0c0',
  color: '#a11414', padding: '4px 10px', borderRadius: 6,
  cursor: 'pointer', fontSize: 12, fontWeight: 500,
};
